import * as angular from 'angular';
import {IController, IPromise, IQService} from 'angular';
import * as _ from 'lodash';
import {SiteFiltersType} from '../../../commons/types/site-filters.type';
import {GroupModel} from '../../../groups/models/group.model';
import {AccountsService} from '../../services/accounts-service.service';
import {AgentAccountAssignmentDialogService} from '../../services/agent-account-assignment-dialog.service';
import {AccountWithPreferredAgents} from '../../models/account-with-preferred-agents.model';
import {ServiceType} from '../../../deliveries/types/service.type';
import {
  AgentLinkItem,
  buildAssignMultiplePayload,
  filterAgentsByCategory,
  getAgentId,
  toAgentLinkItems
} from '../../utils/account-preferred-agents.util';
import {TagType} from '../../../tags/angular/types/tag.type';
import {AgentAssignmentImportType} from '../agent-assignments-import-view/agent-assignments-import-view.component';
import {AgentsService} from '../../../agents/services/agents-service.service';
import {AgentsPaginationDataModel} from '../../../agents/angular/models/agents-pagination-data.model';

export type AssignmentManagerTab = 'delivery' | 'fieldService';

/**
 * CON-14083 - the table opens sorted alphabetically by account name, matching the
 * default order of the Accounts table.
 */
const DEFAULT_SORT = 'name,asc';

export interface AccountAssignmentRow extends AccountWithPreferredAgents {
  primaryAgents?: AgentLinkItem[];
  secondaryAgents?: AgentLinkItem[];
  dedicatedAgents?: AgentLinkItem[];
  restrictedAgents?: AgentLinkItem[];
}

export class AgentAccountAssignmentManagerCtrl implements IController {

  branchIds: string[];
  tags: string[];
  groupIds: string[];
  agentIds: string[];
  segment: number;

  public importTypes: AgentAssignmentImportType[];
  public importPermissions: string[];
  public accountsWithAgents: AccountAssignmentRow[] = [];
  public cursor: string;
  public isLoading: boolean = false;
  public isFetchingNext: boolean = false;
  public errorMessage: string = '';
  public loadLimit: number = 30;
  public currentNavItem: AssignmentManagerTab = 'fieldService';
  public activeTab: AssignmentManagerTab = 'fieldService';
  public serviceType: string = ServiceType.Operation;
  public filtersConfig: any;
  public pageName: string;
  public selectedQuickFilterId: string;
  public groupsBranchIds: string[];
  private scrollContainer: HTMLElement;
  private boundScrollHandler: (event: Event) => void;

  /* @ngInject */
  constructor(
    private $element,
    private $q: IQService,
    private $state,
    private $stateParams,
    private $timeout,
    private $groups,
    private accountsService: AccountsService,
    private agentsService: AgentsService,
    private agentAccountAssignmentDialogService: AgentAccountAssignmentDialogService,
    private accountsConstants
  ) {
    this.pageName = this.$state.current.name;
    this.importTypes = this.accountsConstants.agentAssignmentImportTypes;
    this.importPermissions = _.uniq(_.flatMap(this.importTypes, 'permissions'));
    this.boundScrollHandler = (event: Event) => this.onTableScroll(event);
  }

  $onDestroy() {
    this._unbindScrollListener();
  }

  $onInit() {
    this.filtersConfig = {
      queryString: this.$stateParams.query,
      includeFilters: [
        SiteFiltersType.branchIds,
        SiteFiltersType.groupIds,
        SiteFiltersType.tags,
        SiteFiltersType.segment,
        SiteFiltersType.agentIds
      ],
      bringozFilters: {
        [SiteFiltersType.branchIds]: this.branchIds || [],
        [SiteFiltersType.groupIds]: this.groupIds || [],
        [SiteFiltersType.tags]: this.tags || [],
        [SiteFiltersType.segment]: this.segment,
        [SiteFiltersType.agentIds]: this.agentIds || []
      },
      excludeIdsMap: {
        [SiteFiltersType.branchIds]: this.groupsBranchIds || []
      },
      queryMap: {
        tagType: TagType.Account
      }
    };

    if (this.groupIds?.length) {
      this._resolveGroupBranchIds(this.groupIds).then(() => {
        this._loadAgentTabDataIfNeeded();
      });
    } else {
      this._loadAgentTabDataIfNeeded();
    }
  }

  onQueryChange(query: string) {
    this.filtersConfig.queryString = query;
    this.loadAccounts(true);
    this._setStateParams();
  }

  onFiltersChange(filters, selectedQuickFilter?) {
    this.filtersConfig.bringozFilters = filters;
    this.selectedQuickFilterId = selectedQuickFilter?.id;

    const groupIds = filters[SiteFiltersType.groupIds];
    if (groupIds?.length) {
      this._resolveGroupBranchIds(groupIds).then(() => {
        this.loadAccounts(true);
        this._setStateParams();
      });
    } else {
      this.filtersConfig.excludeIdsMap[SiteFiltersType.branchIds] = [];
      this.loadAccounts(true);
      this._setStateParams();
    }
  }

  onTabChange(tab: AssignmentManagerTab) {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this.currentNavItem = tab;
    this._unbindScrollListener();

    this.serviceType = tab === 'fieldService' ? ServiceType.Operation : ServiceType.Delivery;

    this._loadAgentTabDataIfNeeded(true);
  }

  onEditAssignments(accountWithAgents: AccountAssignmentRow, event: Event) {
    this.agentAccountAssignmentDialogService.showEditAssignmentsDialog(
      event,
      accountWithAgents,
      this.serviceType
    ).then((result) => {
      this._saveAssignments(accountWithAgents, result.serviceType, result.categoryIds);
    }, () => {
    });
  }

  onUploadAgentAssignments(importType: AgentAssignmentImportType) {
    this.$state.go('accounts-agent-assignments-upload', {type: importType.id});
  }

  fetchNext() {
    if (!this.cursor || this.isFetchingNext || this.isLoading) {
      return;
    }
    this.loadAccounts(false);
  }

  onTableScroll(event) {
    const element = event?.target;
    if (!element || !this.cursor || this.isFetchingNext || this.isLoading) {
      return;
    }

    const threshold = 100;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
      this.fetchNext();
    }
  }

  refresh() {
    this.loadAccounts(true);
  }

  private _loadAgentTabDataIfNeeded(enrichOnly?: boolean) {
    if (enrichOnly && this.accountsWithAgents.length) {
      this._enrichAllRows();
      // No refetch on tab switch (serviceType is filtered client-side), but the scroll
      // listener was unbound in onTabChange, so re-attach it.
      this._scheduleScrollBinding();
      return;
    }

    if (!this.accountsWithAgents.length && !this.isLoading) {
      this.loadAccounts(true);
      return;
    }

    this._enrichAllRows();
    this._scheduleScrollBinding();
  }

  private _enrichAllRows() {
    this.accountsWithAgents = _.map(this.accountsWithAgents, (row) => this._enrichRow(row));
  }

  private _enrichRow(row: AccountWithPreferredAgents): AccountAssignmentRow {
    const preferredAgents = row.preferredAgents || [];

    return {
      ...row,
      primaryAgents: toAgentLinkItems(filterAgentsByCategory(preferredAgents, this.serviceType, 'primary')),
      secondaryAgents: toAgentLinkItems(filterAgentsByCategory(preferredAgents, this.serviceType, 'secondary')),
      dedicatedAgents: toAgentLinkItems(filterAgentsByCategory(preferredAgents, this.serviceType, 'dedicated')),
      restrictedAgents: toAgentLinkItems(filterAgentsByCategory(preferredAgents, this.serviceType, 'restricted'))
    };
  }

  private loadAccounts(reset: boolean) {
    if (reset) {
      this.accountsWithAgents = [];
      this.cursor = undefined;
      this.isLoading = true;
    } else {
      this.isFetchingNext = true;
    }

    this.errorMessage = '';

    const params = this._buildQueryParams();

    this.accountsService.getAccountsWithPreferredAgents(params, true)
      .then((response: any) => {
        const parsed = this._parseResponse(response);
        return this._excludeInactiveAgents(parsed.list)
          .then((list) => ({cursor: parsed.cursor, list}));
      })
      .then((parsed) => {
        this.cursor = parsed.cursor?.length ? parsed.cursor : undefined;
        const enrichedList = _.map(parsed.list, (row) => this._enrichRow(row));
        this.accountsWithAgents = reset
          ? enrichedList
          : _.uniqBy([...this.accountsWithAgents, ...enrichedList], (item) => item.accountReference?.id);
      })
      .catch(() => {
        if (reset) {
          this.errorMessage = 'Failed to load agent assignments. Please try again.';
          this.accountsWithAgents = [];
        }
      })
      .finally(() => {
        this.isLoading = false;
        this.isFetchingNext = false;
        this._scheduleScrollBinding();
      });
  }

  private _saveAssignments(accountWithAgents: AccountAssignmentRow, serviceType: string, categoryIds: any) {
    const accountId = accountWithAgents.accountReference?.id;
    if (!accountId) {
      return;
    }

    const payload = buildAssignMultiplePayload(serviceType, categoryIds);
    this.accountsService.assignMultiplePreferredAgents(accountId, payload)
      .then(() => {
        this.loadAccounts(true);
      });
  }

  /**
   * A deactivated agent keeps its account assignments, but the agent picker lists only active
   * agents, so it could never be unselected in the edit dialog. Dropping it from the rows hides it
   * from the table, and the next save unassigns it because assignMultiple replaces the whole set.
   * If the lookup fails the rows are kept as they are.
   */
  private _excludeInactiveAgents(list: AccountWithPreferredAgents[]): IPromise<AccountWithPreferredAgents[]> {
    const agentIds = _.uniq(_.compact(_.flatMap(list, (row) => _.map(row.preferredAgents, getAgentId))));
    if (!agentIds.length) {
      return this.$q.resolve(list);
    }

    return this.agentsService.getAgentsPagination({ids: agentIds, active: false, size: agentIds.length})
      .then((response: AgentsPaginationDataModel) => {
        const inactiveAgentIds = _.map(response?.content, 'id');
        if (!inactiveAgentIds.length) {
          return list;
        }

        return _.map(list, (row) => ({
          ...row,
          preferredAgents: _.reject(row.preferredAgents, (agent) => _.includes(inactiveAgentIds, getAgentId(agent)))
        }));
      })
      .catch(() => list);
  }

  private _getSegmentFilterValue(value: number | string): number | undefined {
    if (_.isNil(value) || value === '') {
      return undefined;
    }

    const segment = Number(value);

    return Number.isNaN(segment) ? undefined : segment;
  }

  private _buildQueryParams() {
    const filters = this.filtersConfig.bringozFilters;
    const branchIds = _.uniq([
      ...(filters[SiteFiltersType.branchIds] || []),
      ...(this.filtersConfig.excludeIdsMap[SiteFiltersType.branchIds] || [])
    ]);

    const segment = this._getSegmentFilterValue(filters[SiteFiltersType.segment]);

    const params: any = {
      branchIds: branchIds.length ? branchIds : undefined,
      tags: filters[SiteFiltersType.tags]?.length ? filters[SiteFiltersType.tags] : undefined,
      agentIds: filters[SiteFiltersType.agentIds]?.length ? filters[SiteFiltersType.agentIds] : undefined,
      segment,
      query: this.filtersConfig.queryString,
      limit: this.loadLimit,
      cursor: this.cursor,
      sort: DEFAULT_SORT,
      isActive: true,
      isArchive: false
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === null || (_.isArray(params[key]) && !params[key].length)) {
        delete params[key];
      }
    });

    return params;
  }

  private _parseResponse(response: any): {cursor?: string, list: AccountWithPreferredAgents[]} {
    if (response?.list) {
      return {
        cursor: response.cursor || response._cursor,
        list: response.list
      };
    }

    if (_.isArray(response)) {
      return {
        cursor: undefined,
        list: response
      };
    }

    if (response?.content) {
      return {
        cursor: response.cursor || response._cursor,
        list: response.content
      };
    }

    return {
      cursor: undefined,
      list: []
    };
  }

  private _resolveGroupBranchIds(groupIds: string[]) {
    return this.$groups().$asyncValues(groupIds)
      .then((groups) => {
        let groupsBranchIds = [];
        groups.forEach((g: GroupModel) => {
          if (g.branchIds?.length) {
            groupsBranchIds = groupsBranchIds.concat(g.branchIds);
          }
        });
        this.filtersConfig.excludeIdsMap[SiteFiltersType.branchIds] = groupsBranchIds;
      });
  }

  private _setStateParams() {
    const filters = this.filtersConfig.bringozFilters;
    this.$state.go('.', {
      branchIds: filters[SiteFiltersType.branchIds],
      groupIds: filters[SiteFiltersType.groupIds],
      tags: filters[SiteFiltersType.tags],
      segment: this._getSegmentFilterValue(filters[SiteFiltersType.segment]),
      agentIds: filters[SiteFiltersType.agentIds],
      query: this.filtersConfig.queryString
    }, {
      notify: false,
      reload: false
    });
  }

  private _scheduleScrollBinding() {
    this.$timeout(() => {
      this._bindScrollListener();
      this._maybeFetchUntilScrollable();
    }, 0);
  }

  private _bindScrollListener() {
    this._unbindScrollListener();

    const scrollEl = this.$element[0].querySelector('.assignment-table-scroll') as HTMLElement;
    if (!scrollEl) {
      return;
    }

    this.scrollContainer = scrollEl;
    this.scrollContainer.addEventListener('scroll', this.boundScrollHandler);
  }

  private _unbindScrollListener() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.boundScrollHandler);
      this.scrollContainer = undefined;
    }
  }

  private _maybeFetchUntilScrollable() {
    if (!this.scrollContainer || !this.cursor || this.isFetchingNext || this.isLoading) {
      return;
    }

    const hasScrollableContent = this.scrollContainer.scrollHeight > this.scrollContainer.clientHeight + 1;
    if (!hasScrollableContent) {
      this.fetchNext();
    }
  }
}

angular
  .module('bringoz.admin.accounts')
  .component('agentAccountAssignmentManager', {
    bindings: {
      branchIds: '<?',
      tags: '<?',
      groupIds: '<?',
      agentIds: '<?',
      segment: '<?'
    },
    template: require('./agent-account-assignment-manager.html').default,
    controller: AgentAccountAssignmentManagerCtrl
  });
